import React from "react";
import { FAINT, GREY2, INK, INDIGO } from "./thModel";

const EV = {
  session_created: "Visit booked", host_started: "Provider opened the room", patient_opened_link: "Patient opened the invite link",
  provider_joined: "Provider joined the call", session_running: "Call started", patient_left: "Patient left the call",
  provider_left: "Provider left the call", session_ended: "Call ended", consent_given: "Patient gave consent",
  intake_submitted: "Patient sent intake answers", document_uploaded: "Patient uploaded a document",
};
const ACT = {
  fetch_transcribe: "Recording fetched and transcribed", fetch_transcribe_rerun: "Recording transcribed again", generate_soap: "SOAP note written",
  run_full_pipeline_manual: "Full pipeline run by hand", run_full_pipeline: "Full pipeline run", recording_playback: "Recording played",
  session_removed: "Visit marked removed", session_stale: "Visit marked stale", session_active: "Visit marked active", send_to_claim: "Encounter saved to Encounter review",
};
const human = (s) => String(s || "event").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
const t = (s) => new Date(s).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" });
const day = (s) => new Date(s).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
const dur = (ms) => { if (ms == null || ms < 0) return null; const s = Math.round(ms / 1000), m = Math.floor(s / 60); return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : m ? `${m} min ${s % 60} s` : `${s} s`; };
const ms = (e) => (e ? new Date(e.at).getTime() : null);

function device(m = {}) {
  if (m.device || m.browser) return [m.browser, m.os || m.platform, m.device].filter(Boolean).join(" · ");
  const ua = m.user_agent || m.userAgent || m.ua; if (!ua) return null;
  const b = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const o = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : null;
  return o ? `${b} on ${o}` : b;
}

function rows(v) {
  let seen = false;
  const ev = (v?.session_events || []).map((e) => {
    const m = e.meta || {}, k = e.event || e.type;
    let label = EV[k] || e.label || human(k);
    if (k === "patient_connected") { label = seen ? "Patient reconnected" : `${m.patient_name || m.display_name || "Patient"} joined`; seen = true; }
    if (k === "chat_message") label = `Message: “${m.text || e.label || ""}”`;
    if (k === "session_created" && m.scheduled_at) label = `Visit booked for ${day(m.scheduled_at)}, ${new Date(m.scheduled_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
    return { at: e.at, label, who: k === "chat_message" ? m.sender || e.role : e.role, ip: m.ip, dev: device(m) };
  });
  const log = (v?.pipeline_log || []).map((l) => ({ at: l.at, label: ACT[l.action] || human(l.action), who: l.actor_email || "staff", ip: l.ip, dev: device(l) }));
  return [...ev, ...log].filter((r) => r.at).sort((a, b) => new Date(a.at) - new Date(b.at));
}

function stats(v) {
  const e = v?.session_events || [], f = (k) => e.find((x) => x.event === k), l = (k) => [...e].reverse().find((x) => x.event === k);
  const pIn = e.find((x) => x.event === "patient_connected"), start = f("session_running") || f("provider_joined"), end = l("session_ended");
  const sch = f("session_created")?.meta?.scheduled_at;
  const late = sch && start ? Math.round((ms(start) - new Date(sch).getTime()) / 60000) : null;
  return {
    start, end,
    call: start ? dur((end ? ms(end) : Date.now()) - ms(start)) + (end ? "" : " so far") : null,
    wait: pIn ? dur((start ? ms(start) : Date.now()) - ms(pIn)) + (start ? "" : " so far") : null,
    late: late == null ? null : late > 1 ? `${late} min late` : late < -1 ? `${-late} min early` : "on time",
  };
}

const Stat = ({ k, v, id }) => <div className="flex-1 min-w-[96px] rounded-[10px] px-3 py-2" style={{ background: "#F7F7F9" }} data-testid={`th-ses-stat-${id}`}><div className="text-[10.5px] uppercase tracking-[.08em] font-bold" style={{ color: FAINT }}>{k}</div><div className="text-[13px] font-semibold tabular-nums mt-0.5" style={{ color: v ? INK : FAINT }}>{v || "—"}</div></div>;
const WHO = { provider: ["Provider", INK], patient: ["Patient", INDIGO], system: ["System", FAINT] };

function Row({ r, i }) {
  const [w, c] = WHO[r.who] || [r.who, GREY2];
  return (
    <div className="flex gap-2 py-1.5 border-t first:border-t-0 text-[12.5px]" style={{ borderColor: "#F1F1F4" }} data-testid={`th-ses-row-${i}`}>
      <span className="w-[78px] flex-none tabular-nums" style={{ color: FAINT }}>{t(r.at)}</span>
      <span className="w-[7px] h-[7px] rounded-full flex-none mt-[6px]" style={r.who === "provider" ? { background: INK } : r.who === "patient" ? { background: INDIGO } : { border: "1.5px solid #B4B4B0" }} />
      <span className="flex-1 min-w-0"><span className="block break-words">{r.label}</span>
        <span className="block text-[11.5px] break-all" style={{ color: FAINT }}><span style={{ color: c, fontWeight: 600 }}>{w}</span>{r.ip ? ` · IP ${r.ip}` : ""}{r.dev ? ` · ${r.dev}` : ""}</span></span>
    </div>
  );
}

export function SessionTimeline({ v, testid = "th-vp-session" }) {
  const all = rows(v), s = stats(v);
  if (!all.length) return <div className="text-[12.5px]" style={{ color: FAINT }} data-testid={testid}>No session events yet.</div>;
  const a = s.start ? ms(s.start) : Infinity, b = s.end ? ms(s.end) : Infinity;
  const groups = [["before", "Before the call", all.filter((r) => ms(r) < a)], ["during", s.end ? "During the call" : "During the call · live", all.filter((r) => ms(r) >= a && ms(r) <= b)], ["after", "After the call", all.filter((r) => ms(r) > b)]].filter((g) => g[2].length);
  const ppl = v?.session_participants || [];
  let n = 0;
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2 mb-3"><Stat k="Call length" v={s.call} id="call" /><Stat k="Waiting room" v={s.wait} id="wait" /><Stat k="Started" v={s.late} id="late" /></div>
      {ppl.length ? <div className="text-[12px] mb-3" style={{ color: GREY2 }} data-testid="th-ses-people">{ppl.map((p, i) => <div key={i}><b style={{ color: INK }}>{p.display_name || p.relationship_label}</b>{p.joined_at ? ` joined ${t(p.joined_at)}` : ""}{p.ip ? ` · IP ${p.ip}` : ""}{device(p) ? ` · ${device(p)}` : ""}</div>)}</div> : null}
      {groups.map(([k, l, list]) => <div key={k} className="mb-3" data-testid={`th-ses-${k}`}><div className="th-lab mb-1">{l} · {day(list[0].at)}</div>{list.map((r) => <Row key={n} r={r} i={n++} />)}</div>)}
    </div>
  );
}
